import Vuex from 'vuex'
import Vue from 'vue'
import { axios } from '../plugins/axios'
import { log } from '../plugins/helpers'
import { getWebcamStream, getScreenRecorderStream, isMobileOrTablet, getAudioStream } from '../plugins/webcam'
import ENV from '../env'

import exam from './exam'
import results from './results'
import survey from './survey'
import review from './review'
import language from './language'
import googleTranslate from './googleTranslate'
import invigilate from './invigilate'

Vue.use(Vuex)

const store = new Vuex.Store({
  namespaced: true,

  state: {
    isLoading: true,
    user_id: null,
    exam_id: null,
    user_email: null,
    first_name: null,
    last_name: null,
    usage_threshold_level: null,
    go_to_user_dash_on_back_click: null,
    artificial_type: localStorage.artificial_type ? localStorage.artificial_type : 'normal',
    is_demo: false,
    invigilate_warning_confirmed: false,

    page: '', // exam, dashboard, review, results, survey, invigilate_setup, pre_start_video
    flags: null,
    dashboard_url: null, // example: certn main dashboard
    alphabets: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p'],

    //  exam api (simulation or real)
    api: {
      SHOW_DASHBOARD: 'api/exam/v1/show-dashboard',
      GET_INITIAL_SURVEY: 'api/exam/v1/get-initial-survey',
      FINISH_OR_TIME_OVER: 'api/exam/v1/exam-finished-or-time-is-over',
      UPDATE_COVERAGES: 'api/exam/v1/update-coverage-tables',
      SHOW_RESULTS: 'api/exam/v1/show-exam-overall-results',
      SHOW_REVIEWS: 'api/exam/v1/show-review-panel',
      START_EXAM: 'api/exam/v1/start-exam',
      UPDATE_QUESTION_FLAG: 'api/exam/v1/update-question-flag',
      UPDATE_CHOICE: 'api/exam/v1/update-choice',
      REQUEST_COACHING: 'api/exam/v1/request-coaching',
      SAVE_SURVEY_RESULTS: 'api/survey/v1/save-survey-result'
    }
  },

  mutations: {
    // Store initial user inputs (props)
    INITIALIZE_STATE(state, props) {
      log('passed input props', props)

      state.user_id = props.user_id
      state.exam_id = props.exam_id
      state.user_email = props.user_email
      state.first_name = props.first_name
      state.last_name = props.last_name
      state.go_to_user_dash_on_back_click = props.go_to_user_dash_on_back_click
      state.is_demo = props.is_demo

      // when user clicks on back button, he will be redirected to dashboard_url
      if (props.user_dashboard_url) {
        state.dashboard_url = props.user_dashboard_url
      } else {
        state.dashboard_url = ENV.dashboard_url
      }

      // $router->post('show-review-panel', 'DemoController@showReviewPanel');

      if (state.is_demo === true) {
        state.api = {
          SHOW_DASHBOARD: 'demo/api/exam/v1/show-dashboard',
          UPDATE_CHOICE: 'demo/api/exam/v1/update-choice',
          UPDATE_QUESTION_FLAG: 'demo/api/exam/v1/update-question-flag',
          FINISH_OR_TIME_OVER: 'demo/api/exam/v1/exam-finished-or-time-is-over',
          SHOW_RESULTS: 'demo/api/exam/v1/show-exam-overall-results',
          SHOW_REVIEWS: 'demo/api/exam/v1/show-review-panel',
          // GET_INITIAL_SURVEY: 'demo/api/exam/v1/get-initial-survey',
          START_EXAM: 'demo/api/exam/v1/start-exam'
        }
      }
    },

    SET_PAGE(state, page) {
      state.page = page
    },

    LOADING(state) {
      state.isLoading = true
    },

    NOT_LOADING(state) {
      state.isLoading = false
    },

    SET_FLAGS(state, value) {
      state.flags = value
    },

    SET_USAGE_THRESHOLD_LEVEL(state, value) {
      state.usage_threshold_level = value
    },

    SET_USER_ID(state, value) {
      state.user_id = value
    },

    SET_ARTIFICIAL_TYPE(state, value) {
      state.artificial_type = value
      localStorage.artificial_type = value
      log('artificial type changed to:', value)
    },

    SET_INVIGILATE_WARNING_CONFIRMED(state, value) {
      state.invigilate_warning_confirmed = value
    }
  },

  getters: {
    examIsInvigilated(state) {
      return !state.is_demo && state.exam.exam_details.invigilated
    }
  },

  actions: {
    async initializeApp({ commit, state, dispatch }, props) {
      if (props) {
        commit('INITIALIZE_STATE', props)
      }

      log('initializing')
      commit('LOADING')

      let { data } = await axios.post(state.api.SHOW_DASHBOARD, {
        user_id: state.user_id,
        exam_id: state.exam_id,
        user_email: state.user_email,
        first_name: state.first_name
      })

      log('dash details from api', data)
      commit('SET_USAGE_THRESHOLD_LEVEL', data.usage_threshold_level)
      commit('SET_FLAGS', data.flags)
      dispatch('exam/setExamDetails', data.exam_details)

      if (data.usage_threshold_level === 3) {
        commit('SET_PAGE', 'threshold_exceeded')
      } else if (state.is_demo) {
        dispatch('initDemoExam', data)
      } else {
        if (data.exam_details.invigilated && (state.flags.middle_of_exam || state.flags.time_is_over)) {
          dispatch('initInvigilateAccess')
          dispatch('setInitialExamData', data)
          dispatch('setInitialExamPage')
        } else if (
          data.exam_details.invigilated &&
          !state.flags.middle_of_exam &&
          !state.flags.time_is_over
        ) {
          dispatch('setInitialExamData', data)
          if (state.invigilate_warning_confirmed) {
            dispatch('initInvigilateAccess')
            dispatch('setInitialExamPage')
          } else {
            commit('SET_PAGE', 'invigilate_warning')
          }
        } else {
          dispatch('setInitialExamData', data)
          dispatch('setInitialExamPage')
        }
      }
      commit('NOT_LOADING')
    },

    confirmInvigilateWarning({ dispatch, commit }) {
      commit('SET_INVIGILATE_WARNING_CONFIRMED', true)
      dispatch('initInvigilateAccess')
      dispatch('setInitialExamPage')
    },

    async initInvigilateAccess({ dispatch, state, rootGetters }) {
      if (isMobileOrTablet()) {
        dispatch('invigilate/setIsMobileOrTablet', true)
      } else {
        if (!state.flags.middle_of_exam) {
          dispatch('invigilate/startUserPicTimer')
        }
        if (!rootGetters['invigilate/webcamAccessFulfilled']) {
          let webcamResult = await getWebcamStream({ quality: 'low' })
          dispatch('invigilate/setWebcam', webcamResult)
        }

        if (!rootGetters['invigilate/audioAccessFulfilled']) {
          let audioResult = await getAudioStream()
          dispatch('invigilate/setAudio', audioResult)
        }

        if (!rootGetters['invigilate/screenRecorderFulfilled']) {
          let screenResult = await getScreenRecorderStream()
          dispatch('invigilate/setScreenRecorder', screenResult)
        }
      }
    },

    setInitialExamData({ commit, dispatch, state }, data) {
      if (state.flags.middle_of_exam) {
        dispatch('exam/setQuestions', data.questions)
        dispatch('language/initMiddleOfExam', {
          exam_language_code: data.exam_lang,
          system_languages: data.system_languages
        })
        commit('SET_PAGE', 'exam')
      } else if (state.flags.time_is_over) {
        dispatch('results/setResults', data.exam_results)
        commit('SET_PAGE', 'results')
        dispatch('updateCoverageTables')
      } else {
        // show dashboard
        dispatch('exam/setProgress', data.progress)
        dispatch('exam/setPreviousAttempts', data.previous_attempts)
        dispatch('language/initializeLanguage', {
          exam_language_codes: data.exam_language_codes,
          system_languages: data.system_languages
        })
        commit('SET_PAGE', 'dashboard')
      }
    },

    setInitialExamPage({ commit, state }) {
      if (state.flags.middle_of_exam) {
        commit('SET_PAGE', 'exam')
      } else if (state.flags.time_is_over) {
        commit('SET_PAGE', 'results')
      } else {
        // show dashboard
        commit('SET_PAGE', 'dashboard')
      }
    },

    initDemoExam({ commit, dispatch }, data) {
      dispatch('language/initializeLanguage', {
        exam_language_codes: data.exam_language_codes,
        system_languages: data.system_languages
      })
      commit('SET_PAGE', 'dashboard')
    },

    updateCoverageTables({ state }) {
      axios
        .post(state.api.UPDATE_COVERAGES, {
          user_id: state.user_id,
          exam_id: state.exam_id
        })
        .then((response) => {
          log('update coverage response', response.data)
        })
        .catch((error) => {
          log('error update coverage:', error)
        })
    },

    async showOverallResults({ commit, dispatch, state }, { exam_instance_id }) {
      commit('LOADING')

      let { data } = await axios.post(state.api.SHOW_RESULTS, {
        user_id: state.user_id,
        exam_id: state.exam_id,
        exam_instance_id: exam_instance_id,
        lang: state.language.selected_language.lang
      })

      // No difference in demo mode
      commit('SET_FLAGS', data.flags)
      dispatch('results/setResults', data.exam_results)
      commit('SET_PAGE', 'results')

      commit('NOT_LOADING')
    },

    async showReviewPanel({ commit, state, dispatch }, { exam_instance_id }) {
      commit('LOADING')

      let { data } = await axios.post(state.api.SHOW_REVIEWS, {
        user_id: state.user_id,
        exam_id: state.exam_id,
        exam_instance_id
      })

      // No difference in demo mode
      dispatch('review/setReviewQuestions', data.review_questions)
      commit('SET_PAGE', 'review')

      commit('NOT_LOADING')
    },

    hideConnectionError() {
      document.getElementById('connection_error').style.display = 'none'
    },
    showConnectionError() {
      document.getElementById('connection_error').style.display = 'block'
    },

    catchAllErrors({ dispatch }, error) {
      if (!error.response) {
        dispatch('showConnectionError')
      }
      log('error:', error)
    },

    showDashboardClicked({ state, dispatch }) {
      if (state.go_to_user_dash_on_back_click) {
        window.location.href = state.dashboard_url
      } else {
        dispatch('initializeApp')
      }
    }
  },

  modules: {
    exam,
    results,
    survey,
    review,
    language,
    googleTranslate,
    invigilate
  }
})

export default store
